import { z } from 'zod';

/**
 * I ruoli dell'azienda (ADR 0207, 0207): composti dalla libreria nella Fase 0, con la forma del ruolo, chi lo copre di
 * solito a questa dimensione e chi lo ricopre davvero (persone assegnate, cariche della visura).
 */
export const ROLE_STATUSES = ['vale', 'da_confermare', 'non_serve'] as const;
export type RoleStatus = (typeof ROLE_STATUSES)[number];

export interface CompanyRoleDto {
  chiave: string;
  nome: string;
  /** organo, incarico_legge, funzione, mestiere; null per un ruolo importato o scritto a mano. */
  tipo: 'organo' | 'incarico_legge' | 'funzione' | 'mestiere' | null;
  area: string | null;
  /** Il reparto dell'area di cui fa parte (ADR 0206 § 10), come in `reparti`; null se sta direttamente nell'area. */
  reparto: string | null;
  risponde_a: string | null;
  descrizione: string | null;
  stato: RoleStatus;
  motivo: string | null;
  di_solito: 'interno' | 'esterno' | 'entrambi' | null;
  esterno_tipico: string | null;
  /** Il ruolo che di solito lo copre a questa dimensione, o `lavoratore`; null se è un posto suo. */
  coperto_da: string | null;
  norma: string | null;
  adempimenti: string[];
  incompatibile: Array<{ ruolo: string; base: 'legge' | 'prassi'; norma: string | null; nota: string }>;
  /** Le sedi in cui vale (nomi); vuoto = tutta l'azienda. */
  sedi: string[];
  /** Le stesse sedi per id (ADR 0210 § 7): dentro una sede si vedono i suoi ruoli. */
  sedi_id: string[];
  /** «base» o il gruppo ATECO; null fuori dalla libreria. */
  strato: string | null;
  origine: 'libreria' | 'altro';
  modificato_dal_titolare: boolean;
  /** Chi lo ricopre secondo la visura. */
  cariche: Array<{ nome: string; carica: string }>;
  /** Le persone dell'azienda assegnate. */
  persone: Array<{ id: string; nome: string }>;
}

export interface CompanyRolesState {
  /** micro, piccola, media o grande, dagli addetti. */
  classe: string;
  aree: Array<{ chiave: string; nome: string; responsabile: string | null }>;
  /** I reparti dei mestieri dell'azienda: `chiave` è `<gruppo>.<reparto>`, il responsabile la chiave di un ruolo. */
  reparti: Array<{ chiave: string; nome: string; area: string; responsabile: string; descrizione: string }>;
  ruoli: CompanyRoleDto[];
  /** Ruoli che valgono e non hanno nessuno: né persone né cariche. */
  scoperti: number;
  da_confermare: number;
}

/** Il titolare cambia un ruolo: non serve, torna a valere, o risponde a un altro ruolo. */
export const RoleUpdateSchema = z
  .object({
    stato: z.enum(['vale', 'non_serve']).optional(),
    risponde_a: z.string().trim().min(1).max(120).nullable().optional(),
  })
  .refine((v) => v.stato !== undefined || v.risponde_a !== undefined, { message: 'Niente da cambiare' });
export type RoleUpdate = z.infer<typeof RoleUpdateSchema>;
